import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Trash2, Download, Upload, Edit2, Check, X } from 'lucide-react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell
} from 'recharts';
import {
  loadScenarios,
  deleteScenario,
  renameScenario,
  exportScenarios,
  importScenarios
} from '../../utils/retirement/scenarioManager';
import { formatChartValue } from '../../utils/chartFormatters';

/**
 * Scenario Comparison Component
 * Displays saved scenarios and allows comparison of up to 3 scenarios
 */
export default function ScenarioComparison({ currencySymbol = '$', onLoadScenario }) {
  const [scenarios, setScenarios] = useState([]);
  const [selectedScenarios, setSelectedScenarios] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');

  useEffect(() => {
    loadSavedScenarios();
  }, []);

  const loadSavedScenarios = () => {
    const saved = loadScenarios();
    setScenarios(saved);
    // Auto-select last 3 scenarios for comparison
    if (saved.length > 0) {
      setSelectedScenarios(saved.slice(0, Math.min(3, saved.length)).map(s => s.id));
    }
  };

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this scenario?')) {
      deleteScenario(id);
      loadSavedScenarios();
      setSelectedScenarios(prev => prev.filter(sid => sid !== id));
    }
  };

  const handleRename = (id) => {
    if (editName.trim()) {
      renameScenario(id, editName);
      loadSavedScenarios();
    }
    setEditingId(null);
    setEditName('');
  };

  const startEditing = (scenario) => {
    setEditingId(scenario.id);
    setEditName(scenario.name);
  };

  const toggleSelection = (id) => {
    setSelectedScenarios(prev => {
      if (prev.includes(id)) {
        return prev.filter(sid => sid !== id);
      } else {
        // Limit to 3 scenarios
        if (prev.length >= 3) {
          return [...prev.slice(1), id];
        }
        return [...prev, id];
      }
    });
  };

  const handleImport = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      importScenarios(file)
        .then((count) => {
          alert(`Successfully imported ${count} scenario(s)`);
          loadSavedScenarios();
        })
        .catch((error) => {
          alert(`Error importing scenarios: ${error.message}`);
        });
    }
  };

  if (scenarios.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Scenario Comparison</CardTitle>
          <CardDescription>No saved scenarios yet</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-12 text-muted-foreground">
            <p className="mb-4">Save your first scenario to start comparing different retirement strategies</p>
            <div className="flex gap-2 justify-center">
              <label htmlFor="import-scenarios">
                <Button variant="outline" size="sm" onClick={() => document.getElementById('import-scenarios').click()}>
                  <Upload className="h-4 w-4 mr-2" />
                  Import Scenarios
                </Button>
              </label>
              <input
                id="import-scenarios"
                type="file"
                accept=".json"
                className="hidden"
                onChange={handleImport}
              />
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const selectedScenarioData = scenarios.filter(s => selectedScenarios.includes(s.id));

  // Prepare data for comparison charts
  const colors = ['#3b82f6', '#8b5cf6', '#f59e0b'];

  // Portfolio balance comparison over time
  const balanceComparisonData = [];
  if (selectedScenarioData.length > 0) {
    const maxYears = Math.max(...selectedScenarioData.map(s => s.results.drawdownProjections?.length || 0));
    for (let year = 0; year < maxYears; year++) {
      const dataPoint = { year: `Year ${year}` };
      selectedScenarioData.forEach((scenario, idx) => {
        const projection = scenario.results.drawdownProjections?.[year];
        dataPoint[scenario.name] = projection?.totalBalance || 0;
      });
      balanceComparisonData.push(dataPoint);
    }
  }

  // Key metrics comparison
  const metricsData = [
    {
      metric: 'Total at Retirement',
      ...selectedScenarioData.reduce((acc, s, idx) => ({
        ...acc,
        [s.name]: s.results.totalAtRetirement
      }), {})
    },
    {
      metric: 'Final Balance',
      ...selectedScenarioData.reduce((acc, s, idx) => ({
        ...acc,
        [s.name]: s.results.finalBalance
      }), {})
    }
  ];

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Saved Scenarios ({scenarios.length})</CardTitle>
              <CardDescription>Select up to 3 scenarios to compare</CardDescription>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={exportScenarios}>
                <Download className="h-4 w-4 mr-2" />
                Export All
              </Button>
              <label htmlFor="import-scenarios-btn">
                <Button variant="outline" size="sm" onClick={() => document.getElementById('import-scenarios-btn').click()}>
                  <Upload className="h-4 w-4 mr-2" />
                  Import
                </Button>
              </label>
              <input
                id="import-scenarios-btn"
                type="file"
                accept=".json"
                className="hidden"
                onChange={handleImport}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {scenarios.map((scenario, idx) => (
              <div
                key={scenario.id}
                className={`p-4 border rounded-lg transition-all cursor-pointer ${
                  selectedScenarios.includes(scenario.id)
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                onClick={() => toggleSelection(scenario.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    {editingId === scenario.id ? (
                      <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="text"
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          className="px-2 py-1 border rounded text-sm"
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleRename(scenario.id);
                            if (e.key === 'Escape') setEditingId(null);
                          }}
                        />
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleRename(scenario.id)}
                        >
                          <Check className="h-4 w-4 text-green-600" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setEditingId(null)}
                        >
                          <X className="h-4 w-4 text-red-600" />
                        </Button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        {selectedScenarios.includes(scenario.id) && (
                          <div
                            className="w-4 h-4 rounded"
                            style={{ backgroundColor: colors[selectedScenarios.indexOf(scenario.id)] }}
                          />
                        )}
                        <h4 className="font-semibold">{scenario.name}</h4>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            startEditing(scenario);
                          }}
                        >
                          <Edit2 className="h-3 w-3" />
                        </Button>
                      </div>
                    )}
                    <p className="text-xs text-muted-foreground mt-1">
                      Saved: {new Date(scenario.timestamp).toLocaleString()}
                    </p>
                  </div>

                  <div className="flex items-center gap-6 mr-4">
                    <div className="text-right">
                      <div className="text-xs text-muted-foreground">Retirement Age</div>
                      <div className="font-medium">{scenario.inputs.retirementAge}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-muted-foreground">Success Rate</div>
                      <div className={`font-medium ${scenario.results.successRate >= 80 ? 'text-green-600' : scenario.results.successRate >= 60 ? 'text-orange-600' : 'text-red-600'}`}>
                        {scenario.results.successRate?.toFixed(1)}%
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-muted-foreground">Depletion Age</div>
                      <div className="font-medium">
                        {scenario.results.depletionAge > 0 ? scenario.results.depletionAge : 'Never'}
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => onLoadScenario && onLoadScenario(scenario.inputs)}
                    >
                      Load
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDelete(scenario.id)}
                    >
                      <Trash2 className="h-4 w-4 text-red-600" />
                    </Button>
                  </div>
                </div>

                {/* Quick stats */}
                <div className="mt-3 grid grid-cols-4 gap-4 pt-3 border-t text-sm">
                  <div>
                    <span className="text-muted-foreground">At Retirement: </span>
                    <span className="font-medium">
                      {formatChartValue(scenario.results.totalAtRetirement, currencySymbol)}
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Strategy: </span>
                    <span className="font-medium capitalize">
                      {scenario.inputs.withdrawalStrategy?.replace('_', ' ')}
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">State Pension: </span>
                    <span className="font-medium">
                      {scenario.inputs.includeStatePension ? '✓' : '✗'}
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Other Income: </span>
                    <span className="font-medium">
                      {(scenario.inputs.rentalProperties?.length > 0 ||
                        scenario.inputs.partTimeWork ||
                        scenario.inputs.annuities?.length > 0 ||
                        scenario.inputs.spouseIncome?.included)
                        ? '✓'
                        : '✗'}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Comparison Charts */}
      {selectedScenarioData.length >= 2 && (
        <>
          {/* Key Metrics Comparison */}
          <Card>
            <CardHeader>
              <CardTitle>Key Metrics Comparison</CardTitle>
              <CardDescription>Compare important retirement metrics across scenarios</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                {selectedScenarioData.map((scenario, idx) => (
                  <div
                    key={scenario.id}
                    className="p-4 rounded-lg border-2"
                    style={{ borderColor: colors[idx] }}
                  >
                    <h4 className="font-semibold mb-3" style={{ color: colors[idx] }}>
                      {scenario.name}
                    </h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Total at Retirement:</span>
                        <span className="font-medium">
                          {formatChartValue(scenario.results.totalAtRetirement, currencySymbol)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Final Balance:</span>
                        <span className="font-medium">
                          {formatChartValue(scenario.results.finalBalance, currencySymbol)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Success Rate:</span>
                        <span className={`font-medium ${scenario.results.successRate >= 80 ? 'text-green-600' : 'text-orange-600'}`}>
                          {scenario.results.successRate?.toFixed(1)}%
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Funding Ratio:</span>
                        <span className="font-medium">
                          {scenario.results.fundingRatio?.toFixed(2)}x
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Replacement Rate:</span>
                        <span className="font-medium">
                          {scenario.results.replacementRate?.toFixed(0)}%
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Portfolio Balance Over Time */}
              <div>
                <h4 className="text-sm font-semibold mb-3">Portfolio Balance Projection (First 10 Years)</h4>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={balanceComparisonData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="year" />
                      <YAxis tickFormatter={(value) => formatChartValue(value, currencySymbol)} />
                      <Tooltip
                        formatter={(value) => `${currencySymbol}${value.toLocaleString(undefined, { maximumFractionDigits: 0 })}`}
                      />
                      <Legend />
                      {selectedScenarioData.map((scenario, idx) => (
                        <Line
                          key={scenario.id}
                          type="monotone"
                          dataKey={scenario.name}
                          stroke={colors[idx]}
                          strokeWidth={2}
                          dot={{ fill: colors[idx], r: 4 }}
                        />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Input Differences */}
          <Card>
            <CardHeader>
              <CardTitle>Input Differences</CardTitle>
              <CardDescription>Key differences in scenario configurations</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 px-4">Parameter</th>
                      {selectedScenarioData.map((scenario, idx) => (
                        <th key={scenario.id} className="text-left py-2 px-4" style={{ color: colors[idx] }}>
                          {scenario.name}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b">
                      <td className="py-2 px-4 font-medium">Current Age</td>
                      {selectedScenarioData.map(s => (
                        <td key={s.id} className="py-2 px-4">{s.inputs.currentAge}</td>
                      ))}
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 px-4 font-medium">Retirement Age</td>
                      {selectedScenarioData.map(s => (
                        <td key={s.id} className="py-2 px-4">{s.inputs.retirementAge}</td>
                      ))}
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 px-4 font-medium">Desired Annual Income</td>
                      {selectedScenarioData.map(s => (
                        <td key={s.id} className="py-2 px-4">
                          {currencySymbol}{s.inputs.desiredAnnualIncome?.toLocaleString()}
                        </td>
                      ))}
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 px-4 font-medium">Withdrawal Strategy</td>
                      {selectedScenarioData.map(s => (
                        <td key={s.id} className="py-2 px-4 capitalize">
                          {s.inputs.withdrawalStrategy?.replace('_', ' ')}
                        </td>
                      ))}
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 px-4 font-medium">Expected Return</td>
                      {selectedScenarioData.map(s => (
                        <td key={s.id} className="py-2 px-4">{s.inputs.expectedReturn}%</td>
                      ))}
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 px-4 font-medium">Include State Pension</td>
                      {selectedScenarioData.map(s => (
                        <td key={s.id} className="py-2 px-4">{s.inputs.includeStatePension ? '✓ Yes' : '✗ No'}</td>
                      ))}
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 px-4 font-medium">Risk Profile</td>
                      {selectedScenarioData.map(s => (
                        <td key={s.id} className="py-2 px-4 capitalize">{s.inputs.riskProfile}</td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
